import React, { createContext, memo, useState, useMemo } from 'react';
import { Redirect } from 'react-router-dom';
import { getConfig } from '@olxeu-a2b/delivery-utils/src/extras';
import Checkout from './checkout';
import { Confirmation } from './order-confirmation';
import { ConfirmationContextProvider } from './order-confirmation/context/confirmationContext';
import { PostingMethodContextProvider } from './order-confirmation/context/postingMethodContext';
import { CONFIRMATION_PATH } from './order-confirmation/sub-routes';
import ConfirmationSuccessPage from './order-confirmation/components/ConfirmationSuccessPage';
import SafeDealSuccessPage from './safe-deal';
import Dac7Content from './dac7/Dac7Content';
import MyOrders from './orders';
import AdminPanel from './admin-panel';
import { trackMapViewBack } from '../utils/trackMapViewBack';
import trackingKeys from '../tracking/trackingKeys';
import BenefitsContainer from './benefits';
import Dac7Profile from './dac7-profile/Dac7Profile';

const { mweb } = getConfig('sites');
const { myProfile } = getConfig('actionPaths');
const { ordersSelling, returns } = getConfig('deliveryPaths');

const { deliveryMapViewBack, deliveryMapView, deliverySellerMapViewBack, deliverySellerMapView } =
  trackingKeys.events.map;

const routes = [
  process.env.A2B_ENV !== 'production' && {
    name: 'admin_panel',
    path: '/admin',
    isPublic: true,
    Component: AdminPanel,
  },
  {
    name: 'safedeal_success',
    path: '/safedeal/success/:adId([0-9]*)/:orderId([0-9A-Fa-f-]*)',
    Component: SafeDealSuccessPage,
    templateInfo: {
      noMobileHeader: true,
    },
  },
  {
    name: 'checkout',
    path: '/checkout/:adId/:map?',
    Component: Checkout,
    templateInfo: match => ({
      title: match.params.map ? 'dlv_page_checkout_map_title' : 'dlv_page_checkout_title',
      subTitle: null,
      noMobileHeader: match.url === '/checkout/success',
    }),
    onBack: (ev, history) => {
      trackMapViewBack(deliveryMapViewBack, deliveryMapView);
      history.goBack();
    },
  },
  {
    name: 'dac7_form',
    path: '/dac7/form',
    Component: Dac7Content,
    templateInfo: {
      noMobileHeader: true,
    },
  },
  {
    name: 'dac7_profile',
    path: '/dac7/profile',
    Component: Dac7Profile,
    templateInfo: {
      noMobileHeader: true,
    },
  },
  {
    name: 'confirmation_success',
    path: '/orders/confirmation/success/:offerId',
    Component: ConfirmationSuccessPage,
    templateInfo: {
      noMobileHeader: true,
    },
  },
  {
    name: 'confirmation',
    path: CONFIRMATION_PATH,
    Component: ({ adId, orderId, routeUrl, history }) => (
      <PostingMethodContextProvider>
        <ConfirmationContextProvider adId={adId} orderId={orderId}>
          <Confirmation adId={adId} orderId={orderId} routeUrl={routeUrl} history={history} />
        </ConfirmationContextProvider>
      </PostingMethodContextProvider>
    ),
    templateInfo: match => ({
      title: match.params.map ? 'dlv_page_confirmation_map_title' : 'dlv_page_confirmation_title',
      subTitle: null,
      onBack: (ev, history) => {
        trackMapViewBack(deliverySellerMapViewBack, deliverySellerMapView);
        history.goBack();
      },
    }),
  },
  {
    name: 'orders_overview',
    path: '/orders',
    Component: () => <Redirect to={ordersSelling} />,
  },
  {
    name: 'returns',
    path: '/returns',
    Component: () => <Redirect to={returns} />,
  },
  {
    name: 'orders_returns_overview',
    path: '/orders/:state/:elementId?',
    Component: MyOrders,
    templateInfo: {
      onBack: (ev, history) => {
        const { pathname } = history.location;
        const isDetails = /\/orders\/(purchases|sales|returns)\/.*/.test(pathname);
        if (!isDetails || !document.referrer.includes(window.location.host)) {
          window.location.href = `${mweb.url}${myProfile}`;
        } else {
          history.goBack();
        }
      },
    },
  },
  {
    name: 'benefits',
    path: '/clnqt34hiuf',
    Component: BenefitsContainer,
    isPublic: true,
    templateInfo: {
      forceDesktop: true,
    },
  },
].filter(route => !!route);

const RoutesContext = createContext();
RoutesContext.displayName = 'RoutesContext';

export const RoutesProvider = memo(({ children }) => {
  const [active, setActive] = useState(null);
  const value = useMemo(
    () => ({
      active,
      setActive,
      routes,
    }),
    [active],
  );

  return <RoutesContext.Provider value={value}>{children}</RoutesContext.Provider>;
});

export const RoutesConsumer = RoutesContext.Consumer;
export default RoutesContext;
